import dayjs from "dayjs";

import { Card } from "@components/cards/card";
import { Pagination } from "@components/pagination";
import { LoadingSkeleton } from "@components/skeleton-loading";
import {
  ITrackReceiveListDTO,
  TrackReceiveConvertSuccess,
  TrackReceiveConvertType,
  TrackReceiveSuccessTypes,
  TrackReceiveTypeTypes,
  trackReceiveSuccessSelect,
  trackReceiveTypeSelect,
} from "@hooks/dtos/track-receive-dto";
import { Masks } from "@hooks/functions/Masks";
import { abbreviateText, formatJsonText } from "@hooks/functions/formatText";
import { ZuToastifyStore } from "@hooks/stores/zustand-toastify-store";
import { api } from "@service/api";
import { useCallback, useEffect, useState } from "react";
import {
  FaArrowLeft,
  FaBroom,
  FaEye,
  FaInfoCircle,
  FaSatelliteDish,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

interface IWebhookRecordResponseDTO {
  total: number;
  totalPages: number;
  list: ITrackReceiveListDTO[];
}

interface IWebhookRecordFilters {
  id: string;
  type: TrackReceiveTypeTypes | "";
  code: string;
  from: string;
  success: TrackReceiveSuccessTypes | undefined;
  startDate: string;
  endDate: string;
}

const masks = new Masks();
const takeOptions = [10, 30, 50, 100];
const initialFilters: IWebhookRecordFilters = {
  id: "",
  type: "",
  code: "",
  from: "",
  success: undefined,
  startDate: "",
  endDate: "",
};

export const WebhookRecords = () => {
  const navigate = useNavigate();
  const {
    actions: { pushToastify },
  } = ZuToastifyStore();

  const [page, setPage] = useState<number>(1);
  const [take, setTake] = useState<number>(10);
  const [recordAmount, setRecordAmount] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(1);

  const [id, setId] = useState("");
  const [type, setType] = useState<TrackReceiveTypeTypes | "">("");
  const [code, setCode] = useState("");
  const [from, setFrom] = useState("");
  const [success, setSuccess] = useState<
    TrackReceiveSuccessTypes | undefined
  >();
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [resultTime, setResultTime] = useState("Todo Período");

  const [appliedFilters, setAppliedFilters] =
    useState<IWebhookRecordFilters>(initialFilters);
  const [isLoading, setIsLoading] = useState(true);
  const [records, setRecords] = useState<ITrackReceiveListDTO[]>([]);
  const [selectedRecord, setSelectedRecord] = useState<ITrackReceiveListDTO>();

  const buildFilterParams = useCallback((filters: IWebhookRecordFilters) => {
    return Object.fromEntries(
      Object.entries({
        ...(filters.id && { id: filters.id }),
        ...(filters.type && { type: filters.type }),
        ...(filters.code && { code: filters.code }),
        ...(filters.from && { from: filters.from }),
        ...(filters.success && { success: filters.success }),
        ...(filters.startDate && { startDate: filters.startDate }),
        ...(filters.endDate && { endDate: filters.endDate }),
      }),
    );
  }, []);

  const getWebhookRecords = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = `?${new URLSearchParams(
        Object.entries({
          page: (page || 1).toString(),
          take: take.toString(),
          ...buildFilterParams(appliedFilters),
        }),
      ).toString()}`;

      const result = await api.get<IWebhookRecordResponseDTO>(
        `/customer/management/webhook/records${params}`,
      );

      setRecordAmount(result.data.total);
      setRecords(result.data.list);
      setTotalPages(result.data.totalPages || 1);
      setResultTime(
        appliedFilters.startDate.length + appliedFilters.endDate.length > 0
          ? "Período Selecionado"
          : "Todo Período",
      );
    } catch (error: any) {
      console.error(error);
      pushToastify({
        message:
          error?.response?.data?.message ||
          error?.message ||
          "Houve um erro ao consultar os registros de webhook!",
        type: "error",
        theme: "dark",
        pauseOnHover: true,
      });
    } finally {
      setIsLoading(false);
    }
  }, [appliedFilters, buildFilterParams, page, pushToastify, take]);

  const handleFilter = () => {
    setPage(1);
    setAppliedFilters({
      id,
      type,
      code,
      from,
      success,
      startDate,
      endDate,
    });
  };

  const cleanFilters = () => {
    setId("");
    setType("");
    setCode("");
    setFrom("");
    setSuccess(undefined);
    setStartDate("");
    setEndDate("");
    setPage(1);
    setTotalPages(1);
    setAppliedFilters(initialFilters);
  };

  useEffect(() => {
    getWebhookRecords();
  }, [getWebhookRecords]);

  return (
    <>
      <div
        className="container-fluid d-flex flex-column justify-content-start align-items-center"
        style={{
          paddingBottom: "2.5rem",
          paddingTop: "clamp(1rem, 2vw, 2rem)",
          paddingLeft: "clamp(1rem, 5vw, 10rem)",
          paddingRight: "clamp(1rem, 5vw, 10rem)",
        }}
      >
        <div className="col-12">
          <Card className="shadow-sm rounded mb-4 border-0">
            <div className="card-body p-4">
              <div className="d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center gap-3">
                <div className="d-flex align-items-center gap-3">
                  <div className="rounded-3 d-inline-flex align-items-center justify-content-center p-3 bg-info-subtle text-info">
                    <FaSatelliteDish size={30} />
                  </div>

                  <div>
                    <span className="badge rounded-pill text-bg-info mb-2">
                      Comunicação
                    </span>
                    <h3 className="fw-bold mb-1">Webhook</h3>
                    <p className="text-muted mb-0">
                      Consulte recebimentos, integrações e retornos registrados
                      em track_receive.
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  className="btn btn-outline-secondary d-flex align-items-center gap-2"
                  onClick={() => navigate("/management")}
                >
                  <FaArrowLeft />
                  Voltar
                </button>
              </div>
            </div>
          </Card>

          <div className="row">
            <div className="col-md-12">
              <Card>
                <div className="card-header">
                  <div className="card-title">Filtrar Registros</div>
                  <div className="card-category">
                    Utilize o filtro para buscar recebimentos por origem,
                    código, tipo, status ou período.
                  </div>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-12 col-sm-6 col-lg-2">
                      <div className="form-group mb-0">
                        <label htmlFor="id">ID</label>
                        <input
                          id="id"
                          type="number"
                          className="form-control"
                          value={id}
                          onChange={(event) => setId(event.target.value)}
                        />
                        {/* <small className="text-muted">
                          Digite e clique em Filtrar.
                        </small> */}
                      </div>
                    </div>

                    <div className="col-12 col-sm-6 col-lg-2">
                      <div className="form-group mb-0">
                        <label htmlFor="code">Nº Pedido</label>
                        <input
                          id="code"
                          type="number"
                          className="form-control"
                          value={code}
                          onChange={(event) => setCode(event.target.value)}
                        />
                      </div>
                    </div>

                    <div className="col-12 col-sm-6 col-lg-2">
                      <div className="form-group mb-0">
                        <label htmlFor="type">Tipo</label>
                        <select
                          id="type"
                          className="form-control"
                          value={type}
                          onChange={(event) =>
                            setType(
                              event.target.value as TrackReceiveTypeTypes | "",
                            )
                          }
                        >
                          <option value="">Todos</option>
                          {trackReceiveTypeSelect.map((item) => (
                            <option key={item.type} value={item.type}>
                              {item.text}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Source */}
                    {/* <div className="col-12 col-sm-6 col-lg-2">
                      <div className="form-group mb-0">
                        <label htmlFor="from">Origem</label>
                        <input
                          id="from"
                          type="text"
                          className="form-control"
                          placeholder="Zoho..."
                          value={from}
                          onChange={(event) => setFrom(event.target.value)}
                        />
                      </div>
                    </div> */}

                    <div className="col-12 col-sm-6 col-lg-2">
                      <div className="form-group mb-0">
                        <label htmlFor="success">Resultado</label>
                        <select
                          id="success"
                          className="form-control"
                          value={success || ""}
                          onChange={(event) => {
                            const value = event.target.value;
                            setSuccess(
                              value
                                ? (value as TrackReceiveSuccessTypes)
                                : undefined,
                            );
                          }}
                        >
                          <option value="">Todos</option>
                          {trackReceiveSuccessSelect.map((item) => (
                            <option key={item.success} value={item.success}>
                              {item.text}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="col-12 col-sm-6 col-lg-2">
                      <div className="form-group mb-0">
                        <label htmlFor="startDate">Data Inicial</label>
                        <input
                          id="startDate"
                          type="date"
                          className="form-control"
                          value={startDate}
                          onChange={(event) => setStartDate(event.target.value)}
                        />
                      </div>
                    </div>

                    <div className="col-12 col-sm-6 col-lg-2">
                      <div className="form-group mb-0">
                        <label htmlFor="endDate">Data Final</label>
                        <input
                          id="endDate"
                          type="date"
                          className="form-control"
                          value={endDate}
                          onChange={(event) => setEndDate(event.target.value)}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row mt-3">
                    <div className="col-12 d-flex justify-content-end gap-2">
                      <button
                        className="btn btn-sm btn-success px-5"
                        onClick={handleFilter}
                      >
                        <span className="text-white">Filtrar</span>
                      </button>
                      <button
                        className="btn btn-sm btn-warning"
                        onClick={cleanFilters}
                        title="Limpar todos os filtros."
                      >
                        <FaBroom className="text-white" />
                      </button>
                    </div>
                  </div>
                </div>
                <span className="position-absolute end-0 bg-info text-white p-2 rounded-end rounded-bottom-0">
                  {resultTime}
                </span>
              </Card>
            </div>
          </div>

          <div className="row">
            <div className="col-md-12">
              <Card>
                <div className="card-header">
                  <div className="row">
                    <div className="col-md-7">
                      <h4 className="card-title">Registros de Webhook</h4>
                      <p className="card-category">
                        Lista de recebimentos registrados (Track Receive).
                      </p>
                    </div>
                    <div className="col-md-5 d-flex flex-column flex-md-row justify-content-md-end align-items-md-center gap-2 mt-3 mt-md-0">
                      <div
                        className="input-group input-group-sm"
                        style={{ maxWidth: 210 }}
                      >
                        <label className="input-group-text" htmlFor="take">
                          Exibir
                        </label>
                        <select
                          id="take"
                          className="form-select"
                          value={take}
                          onChange={(event) => {
                            setPage(1);
                            setTake(Number(event.target.value));
                          }}
                        >
                          {takeOptions.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                        <span className="input-group-text">por página</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card-body">
                  <div className="table-responsive">
                    <div className="dataTables_wrapper container-fluid dt-bootstrap4">
                      <div className="dataTables_length">
                        <table className="table table-striped">
                          <thead>
                            <tr role="row">
                              <th>ID</th>
                              <th>Tipo</th>
                              <th>Nº Pedido</th>
                              <th>Origem</th>
                              <th>Resultado</th>
                              <th>Mensagem</th>
                              <th>Criação</th>
                              <th>Atualização</th>
                              <th>Ações</th>
                            </tr>
                          </thead>
                          <tbody>
                            {isLoading ? (
                              <tr>
                                <td colSpan={9} className="text-center">
                                  <LoadingSkeleton />
                                </td>
                              </tr>
                            ) : (
                              <>
                                {records.length > 0 &&
                                  records.map((record) => {
                                    const convertedSuccess =
                                      TrackReceiveConvertSuccess(
                                        record.success,
                                      );
                                    const convertedType =
                                      TrackReceiveConvertType(record.type);

                                    return (
                                      <tr key={record.id}>
                                        <td>{record.id}</td>
                                        <td title={convertedType.description}>
                                          {convertedType.text}
                                        </td>
                                        <td>{record.code || "~~"}</td>
                                        <td>{record.from || "~~"}</td>
                                        <td>
                                          <span
                                            className={`fs-6 badge badge-${convertedSuccess.color}`}
                                            title={convertedSuccess.description}
                                          >
                                            {convertedSuccess.text}
                                          </span>
                                        </td>
                                        <td title={record.message || "~~"}>
                                          {abbreviateText(record.message, 45)}
                                        </td>
                                        <td>
                                          {dayjs(record.createdAt).format(
                                            "DD/MM/YYYY HH:mm",
                                          )}
                                        </td>
                                        <td>
                                          {dayjs(record.updatedAt).format(
                                            "DD/MM/YYYY HH:mm",
                                          )}
                                        </td>
                                        <td className="text-center">
                                          <button
                                            type="button"
                                            title="Ver detalhes"
                                            className="badge badge-info border-0"
                                            onClick={() =>
                                              setSelectedRecord(record)
                                            }
                                          >
                                            <FaEye className="fs-5" />
                                          </button>
                                        </td>
                                      </tr>
                                    );
                                  })}

                                {records.length === 0 && (
                                  <tr>
                                    <td colSpan={9} className="text-center">
                                      Nenhum registro encontrado.
                                    </td>
                                  </tr>
                                )}
                              </>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  <div className="d-flex justify-content-between align-items-center">
                    <span>
                      Quantidade de Registros:{" "}
                      {masks.formatMilhar(recordAmount)}
                    </span>
                    <Pagination
                      page={page}
                      setPage={setPage}
                      totalPages={totalPages}
                      maxPageShow={10}
                    />
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {selectedRecord && (
        <>
          <div
            className="modal fade show"
            style={{ display: "block" }}
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            aria-labelledby="webhookRecordModalLabel"
          >
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5
                    className="modal-title fw-bold"
                    id="webhookRecordModalLabel"
                  >
                    Detalhes do Recebimento
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Fechar"
                    onClick={() => setSelectedRecord(undefined)}
                  />
                </div>
                <div className="modal-body">
                  <div className="row g-3 mb-3">
                    <div className="col-12 col-md-3">
                      <small className="text-muted">ID</small>
                      <div className="fw-bold">{selectedRecord.id}</div>
                    </div>
                    <div className="col-12 col-md-3">
                      <small className="text-muted">Tipo</small>
                      <div className="fw-bold">
                        {TrackReceiveConvertType(selectedRecord.type).text}
                      </div>
                      <small className="text-muted">
                        {
                          TrackReceiveConvertType(selectedRecord.type)
                            .description
                        }
                      </small>
                    </div>
                    <div className="col-12 col-md-3">
                      <small className="text-muted">Resultado</small>
                      <div>
                        <span
                          className={`fs-6 badge badge-${
                            TrackReceiveConvertSuccess(selectedRecord.success)
                              .color
                          }`}
                        >
                          {
                            TrackReceiveConvertSuccess(selectedRecord.success)
                              .text
                          }
                        </span>
                      </div>
                    </div>
                    <div className="col-12 col-md-3">
                      <small className="text-muted">Nº Pedido</small>
                      <div className="fw-bold">{selectedRecord.code}</div>
                    </div>
                    <div className="col-12 col-md-3">
                      <small className="text-muted">Origem</small>
                      <div className="fw-bold">{selectedRecord.from}</div>
                    </div>
                  </div>

                  <div className="alert alert-light border d-flex align-items-start gap-2 mb-0">
                    <FaInfoCircle className="mt-1 text-info" />
                    <div>
                      <div className="fw-bold mb-1">Mensagem</div>
                      <div className="text-break">
                        {selectedRecord.message || "~~"}
                      </div>
                    </div>
                  </div>

                  <div className="mt-3">
                    <div className="fw-bold mb-2">Dados recebidos</div>
                    <pre
                      className="bg-light border rounded p-3 mb-0"
                      style={{
                        maxHeight: 420,
                        overflow: "auto",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {formatJsonText(selectedRecord.data)}
                    </pre>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setSelectedRecord(undefined)}
                  >
                    Fechar
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}
    </>
  );
};
